import { config } from '@iheartjane/config';
import type { AeropayBankAccount } from '@iheartjane/data-access';
import {
  useAeropayBankConnection,
  useAeropayUser,
  useConfirmJaneGoldUser,
  useCreateAeropayUser,
  useDeleteAeropayBankAccount,
  useLinkAeropayBankAccount,
  useSelectAeropayBankAccount,
} from '@iheartjane/data-access/react-query';
import type { BankLinkingEvent } from '@iheartjane/menu';
import {
  AeropayScreen,
  AlreadyEnrolledScreen,
  DeleteBankAccountScreen,
  FailureScreen,
  JaneGoldPromptScreen,
  LinkBankScreen,
  MoreInfoScreen,
  NewAccountLinkedScreen,
  SCREEN_NAMES,
  SuccessScreen,
  WIDGET_CONTAINER_ID,
  WelcomeBackScreen,
  getMfaContactDisplay,
  useAerosync,
  useBankLinkingModalData,
  useMfa,
} from '@iheartjane/menu';
import { Box, Loading, Modal } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import type { Nullable } from '@iheartjane/types';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useIframeScroll, useIsInIframe } from '@jane/shared-ecomm/hooks';
import type { CheckoutLocationState } from '@jane/shared-ecomm/providers';
import {
  linkBankAccountEvent,
  moeLinkedFirstBankAccount,
  useMoengage,
} from '@jane/shared-ecomm/tracking';
import type { CalloutVariants } from '@jane/shared-ecomm/types';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser, useUpdateJaneUser } from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';
import { useEcommApp } from '@jane/shared/providers';

import styles from './bankLinkingModal.module.css';

export interface BankLinkingModalProps {
  /** Flag is true when user is adding an additional bank account */
  addNewAccount?: boolean;

  /** Bank account selected for deletion */
  deleteBankAccount?: AeropayBankAccount | null;

  /** When true, hides Jane Pay logos and branding. */
  hideJanePayBranding?: boolean;

  /** Optional boolean to specify when user is linking their bank while at a store with gold enabled */
  isGold?: boolean;

  /** Optional boolean to specify when we're targeting Jane Pay over Jane Gold */
  isJanePay?: boolean;

  /** Flag used for displaying a different message in Delete Bank Account modal */
  isLastBankAccount?: boolean;

  /** Location where the modal was opened, used for tracking */
  location?: CalloutVariants;

  /** Function called when modal is closed */
  onRequestClose(): void;

  /** Boolean for whether or not the modal is open */
  open: boolean;

  /** Flag is true when modal should start with JaneGoldPromptScreen */
  showPrompt?: boolean;

  /** For white labelling Jane Pay */
  storePhoto?: string | null;
}

export const BankLinkingModal = ({
  addNewAccount = false,
  deleteBankAccount = null,
  hideJanePayBranding = false,
  isJanePay = false,
  isGold = false,
  isLastBankAccount = false,
  location,
  onRequestClose,
  open,
  showPrompt = false,
  storePhoto,
}: BankLinkingModalProps) => {
  const [error, setError] = useState<unknown | null>(null);
  const isInIframe = useIsInIframe();
  const { appMode } = useEcommApp();
  const { moeTrack } = useMoengage();
  const showGold = useShouldShowGold();
  const state: Nullable<CheckoutLocationState> = useLocation().state;
  const preAuthCustomer = state?.preAuthCustomer;

  const jwt = getJwt();
  const {
    data: janeUser,
    isFetched: janeUserFetched,
    error: janeUserError,
  } = useJaneUser();
  const janeUserId = janeUser?.user?.id;
  const { getNewPhone, requiresMfa, setNewPhone } = useMfa();
  const usesEmail = !!config.useEmailOverSMS && !config.mfaForceSMS;
  const contactDisplay = getMfaContactDisplay({
    user: janeUser?.user,
    newPhone: getNewPhone(),
    usesEmail,
  });
  const { data: aeropayUser, error: aeropayUserError } = useAeropayUser({
    janeUserId: janeUserId || null,
    jwt,
  });

  const { mutate: updateJaneUser } = useUpdateJaneUser();
  const { mutate: confirmJaneGold } = useConfirmJaneGoldUser({
    janeUserId: janeUserId!,
    jwt,
  });
  const { mutate: createAeropayUser } = useCreateAeropayUser({
    janeUserId: janeUserId!,
    jwt,
  });
  const { mutate: deleteAeropayBankAccount } = useDeleteAeropayBankAccount({
    janeUserId: janeUserId!,
    jwt,
  });
  const { mutate: selectAeropayBankAccount } = useSelectAeropayBankAccount({
    janeUserId: janeUserId!,
    jwt,
  });
  const { mutate: linkAeropayBankAccount } = useLinkAeropayBankAccount({
    janeUserId: janeUserId!,
    jwt,
  });
  const { data: aeropayBankConnection } = useAeropayBankConnection({
    aeropayUserId: aeropayUser?.user_id,
    jwt,
  });

  const handleTrackEvent = (event: BankLinkingEvent) => {
    switch (event.kind) {
      case 'unlinkedLastBankAccount':
        linkBankAccountEvent({
          email: event.email,
          event: EventNames.UnlinkedLastBankAccount,
          username: event.username,
        });
        return;
      case 'linkedFirstBankAccount':
        moeTrack(
          moeLinkedFirstBankAccount({
            email: event.email,
            isGold,
            isJanePay: event.isJanePay,
            source: event.source,
            username: event.username,
          })
        );
        linkBankAccountEvent({
          email: event.email,
          event: EventNames.LinkedFirstBankAccount,
          isJanePay: event.isJanePay,
          source: event.source as CalloutVariants | undefined,
          username: event.username,
        });
        return;
      case 'linkedNewBankAccount':
        linkBankAccountEvent({
          email: event.email,
          event: EventNames.LinkedNewBankAccount,
          isJanePay: event.isJanePay,
          source: event.source as CalloutVariants | undefined,
          username: event.username,
        });
        return;
      case 'linkBankAccountFailed':
        linkBankAccountEvent({
          email: event.email,
          event: EventNames.LinkBankAccountFailed,
          username: event.username,
        });
        return;
    }
  };

  const {
    afterVerifySuccess,
    deletingBankAccount,
    handleClose,
    handleCloseWithOptOut,
    handleDeleteBankAccount,
    handleSubmitInfo,
    needsMoreFlag,
    screen,
    setScreen,
  } = useBankLinkingModalData({
    addNewAccount,
    deleteBankAccount,
    isJanePay,
    onRequestClose,
    location,
    showPrompt,
    setError,
    janeUser: janeUserFetched ? janeUser : null,
    aeropayUser,
    janeUserError,
    aeropayUserError,
    onUpdateJaneUser: updateJaneUser,
    onCreateAeropayUser: createAeropayUser,
    onDeleteAeropayBankAccount: deleteAeropayBankAccount,
    onSelectAeropayBankAccount: selectAeropayBankAccount,
    onSetNewPhone: setNewPhone,
    onConfirmJaneGoldUser: confirmJaneGold,
    onTrackEvent: handleTrackEvent,
  });

  const handleError = (err: unknown) => {
    setError(err);
    setScreen(SCREEN_NAMES.error);
  };

  const {
    isLoading: isLoadingWidget,
    linkingBankAccount,
    widgetRef,
  } = useAerosync({
    aeropayConsumerId: config.aeropayConsumerId,
    aeropayEnv: config.aeropayEnv,
    token: aeropayBankConnection?.token,
    onClose: handleClose,
    onError: handleError,
    onLinkBankAccount: linkAeropayBankAccount,
    onSuccess: () =>
      setScreen(
        addNewAccount ? SCREEN_NAMES.newAccountSuccess : SCREEN_NAMES.success
      ),
    widgetContainerId: WIDGET_CONTAINER_ID,
  });

  function renderSwitch() {
    const {
      aeropay,
      alreadyEnrolled,
      deleteBankAccountScreen,
      error: errorScreen,
      linkBank,
      loading,
      moreInfo,
      moreInfoAeropay,
      newAccountSuccess,
      promptLinkBank,
      success,
      welcomeBack,
    } = SCREEN_NAMES;

    switch (screen) {
      case loading:
        return (
          <Box py={48} position="relative">
            <Loading color="black" size="lg" />
          </Box>
        );
      case welcomeBack:
        return <WelcomeBackScreen onDoneClick={handleClose} />;
      case alreadyEnrolled:
        return (
          <AlreadyEnrolledScreen
            hideJanePayBranding={hideJanePayBranding}
            isJanePay={isJanePay}
            onDoneClick={handleClose}
            storePhoto={storePhoto}
          />
        );
      case moreInfo:
      case moreInfoAeropay:
        return (
          <MoreInfoScreen
            onDoneClick={handleClose}
            onSubmit={handleSubmitInfo}
            user={{
              firstName:
                janeUser?.user?.first_name || preAuthCustomer?.firstName,
              lastName: janeUser?.user?.last_name || preAuthCustomer?.lastName,
              phone: janeUser?.user?.phone || undefined,
            }}
          />
        );
      case promptLinkBank:
        return (
          <JaneGoldPromptScreen
            hideJanePayBranding={hideJanePayBranding}
            onContinue={() =>
              needsMoreFlag ? setScreen(moreInfoAeropay) : setScreen(linkBank)
            }
            onRequestClose={handleClose}
          />
        );
      case linkBank:
        return (
          <LinkBankScreen
            hideJanePayBranding={hideJanePayBranding}
            isJanePay={isJanePay}
            onContinueClick={() => {
              setScreen(aeropay);
            }}
            onDoneClick={handleClose}
            shouldShowGold={showGold}
          />
        );
      case deleteBankAccountScreen:
        return (
          <DeleteBankAccountScreen
            isRemoving={deletingBankAccount}
            lastBankAccount={isLastBankAccount}
            onRemoveClick={handleDeleteBankAccount}
            onCancelClick={handleClose}
          />
        );
      case aeropay:
        return (
          <AeropayScreen
            contactDisplay={contactDisplay}
            isLinkingBankAccount={linkingBankAccount}
            isLoadingWidget={isLoadingWidget}
            onCancel={handleClose}
            onVerified={afterVerifySuccess}
            requiresMfa={requiresMfa()}
            usesEmail={usesEmail}
            widgetRef={widgetRef}
          />
        );
      case success:
        return (
          <SuccessScreen
            hideJanePayBranding={hideJanePayBranding}
            storePhoto={storePhoto}
            isJanePay={isJanePay}
            isMarketplace={appMode === 'default'}
            location={location}
            onDoneClick={handleCloseWithOptOut}
          />
        );
      case newAccountSuccess:
        return <NewAccountLinkedScreen onDoneClick={handleClose} />;
      case errorScreen:
        return (
          <FailureScreen
            error={error}
            onDoneClick={handleClose}
            onTryAgainClick={() => setScreen(aeropay)}
          />
        );
    }
  }

  const useScroll =
    isInIframe &&
    open &&
    (location === 'cart' || location === 'checkout' || location === 'activity');

  useIframeScroll(useScroll);

  return (
    <Modal
      variant="dialogue"
      open={open}
      onRequestClose={onRequestClose}
      overlayClose
      overlayClassName={styles['overlay']}
      topOverride={isInIframe ? '160px' : undefined}
    >
      <Modal.Content>
        <Box maxWidth={screen === SCREEN_NAMES.aeropay ? 'auto' : '279px'}>
          {renderSwitch()}
        </Box>
      </Modal.Content>
    </Modal>
  );
};
